import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { v4 as uuid } from "uuid";
import moment from "moment";
import { Select, RangePicker, color as colors, theme } from "@workboard/wobo-ui";
import Template from "./TableBreakdown/components/Presets";

const Label = styled("span")``;

const filterId = uuid();

const Filter = memo(
  ({
    className,
    id,
    format = "MM/DD/YYYY",
    label,
    // eslint-disable-next-line
    onChange = () => {},
    options = []
  }) => {
    const { t } = useTranslation();
    const [end, setEnd] = useState();
    const [start, setStart] = useState();
    const [showRangePicker, setShowRangePicker] = useState(false);
    const [key, setKey] = useState(uuid());

    const handleChange = useCallback(
      (ev, { customPeriod, ...option }) => {
        if (customPeriod) {
          setShowRangePicker(true);
        } else {
          setShowRangePicker(false);
          onChange(option);
        }
        setStart(option.start);
        setEnd(option.end);
      },
      [onChange, setEnd, setShowRangePicker, setStart]
    );

    const endDate = useMemo(
      () =>
        moment(end)
          .startOf("days")
          .toDate(),
      [end]
    );
    const startDate = useMemo(
      () =>
        moment(start)
          .startOf("days")
          .toDate(),
      [start]
    );

    const custom = useMemo(
      () => options.find(({ customPeriod }) => customPeriod),
      [options]
    );

    const value =
      options.find(({ start: s, end: e }) => start === s && end === e) ||
      options[0];

    useEffect(() => {
      if (options.length) {
        setStart(options[0].start);
        setEnd(options[0].end);
        onChange(options[0]);
      }
    }, [options]);

    const handleRangeChange = useCallback(
      ({ start: s, end: e }) => {
        const formattedEnd = e ? moment(e) : undefined;
        const formattedStart = s ? moment(s) : undefined;

        if (formattedStart && formattedEnd) {
          if (formattedStart.isAfter(formattedEnd)) {
            setStart(formattedEnd.format(format));
            setEnd(null);
          } else {
            setStart(formattedStart.format(format));
            setEnd(formattedEnd.format(format));
          }
        }
      },
      [format, setStart, setEnd]
    );

    const handleDismiss = useCallback(() => {
      setShowRangePicker(false);
      setStart(options[0].start);
      setEnd(options[0].end);
    }, [options, setEnd, setStart, setShowRangePicker]);

    const handleSubmit = useCallback(() => {
      const formattedEnd = end ? moment(end).format(format) : undefined;
      const formattedStart = start ? moment(start).format(format) : undefined;
      onChange({ ...custom, start: formattedStart, end: formattedEnd });
      setKey(uuid());
    }, [custom, format, onChange, start, end]);

    const Presets = useCallback(
      props => (
        <Template
          {...props}
          onDismiss={handleDismiss}
          onSubmit={handleSubmit}
        />
      ),
      [handleDismiss, handleSubmit]
    );

    return (
      <div className={className}>
        <Label id={filterId}>{label || `${t("OKR")} Period`}</Label>
        {showRangePicker ? (
          <RangePicker
            aria-describedby={filterId}
            // data-testid='select-custom-time-period' // TODO.
            key={key}
            displayFormat={() => 'MMM D, YYYY'}
            start={startDate}
            end={endDate}
            // TODO
            // isOutsideRange={day => {
            //   const minDate = min < start ? moment(min) : null, maxDate = max > end ? moment(max) : null;
            //   return day.isBefore(minDate) || day.isAfter(maxDate);
            // }}
            onChange={handleRangeChange}
            showClearDates={false}
            presetsTemplate={Presets}
            onOutsideClick={handleDismiss}
            keepOpen
            anchorDirection="right"
          />
        ) : (
          <Select
            aria-describedby={filterId}
            data-testid='select-time-period'
            key={id}
            options={options}
            getOptionLabel={({ name }) => name}
            onChange={handleChange}
            panelProps={{
              width: '200px',
            }}
            value={value}
          />
        )}
      </div>
    );
  }
);

export default styled(Filter)`
  align-items: center;
  display: flex;

  ${Label} {
    transform: translateY(4px);
    ${theme('--font-normal')};
    color: ${colors('--black-opacity-60')};
    font-weight: bold;
    margin-right: 12px;
  }

  ${RangePicker}, ${Select} {
    margin-left: 16px;
    z-index: 2;
  }

  ${RangePicker} {
    & .DateInput {
      input {
        width: 85px;
      }
    }
  }

  ${Select} {
    width: 190px;
  }
`;
